/**
 * Type adapters to bridge between ScheduleBoard domain types and CustomScheduler component types
 * This eliminates the need for 'as unknown as any' type assertions
 */

import type {
  AppointmentAddingEvent as SchedulerAppointmentAddingEvent,
  AppointmentComponentProps,
  AppointmentDraggingStartEvent as SchedulerAppointmentDraggingStartEvent,
} from 'pos-client/components/customScheduler/types/schedulerTypes';
import type {
  ScheduleBoardBlockedSlotModel,
  ScheduleBoardJobModel,
} from 'pos-shared/models/schedulingSchema';

import type {
  AppointmentAddingEvent as BoardAppointmentAddingEvent,
  AppointmentDraggingStartEvent as BoardAppointmentDraggingStartEvent,
} from './boardTypes';

// Union type for schedule board data
export type ScheduleBoardAppointment =
  | ScheduleBoardJobModel
  | ScheduleBoardBlockedSlotModel;

// Type guard to check if appointment is a job
export function isScheduleBoardJob(
  appointment: ScheduleBoardAppointment
): appointment is ScheduleBoardJobModel {
  return 'jobId' in appointment;
}

// Type guard to check if appointment is a blocked slot
export function isScheduleBoardBlockedSlot(
  appointment: ScheduleBoardAppointment
): appointment is ScheduleBoardBlockedSlotModel {
  return !('jobId' in appointment) && 'text' in appointment;
}

// Adapter for Slot component props
export interface SlotComponentProps
  extends Omit<AppointmentComponentProps, 'data'> {
  data: {
    appointmentData?: ScheduleBoardAppointment;
  } & ScheduleBoardAppointment;
}

// Event adapter functions to convert between scheduler and board event types
export function adaptDragStartEvent(
  schedulerEvent: SchedulerAppointmentDraggingStartEvent
): BoardAppointmentDraggingStartEvent {
  return {
    cancel: schedulerEvent.cancel,
    component: schedulerEvent.component as React.ComponentType<unknown>,
    fromData: schedulerEvent.itemData as ScheduleBoardJobModel,
    itemData: schedulerEvent.itemData as ScheduleBoardJobModel,
    itemElement: schedulerEvent.itemElement,
  };
}

export function adaptAddEvent(
  schedulerEvent: SchedulerAppointmentAddingEvent
): BoardAppointmentAddingEvent {
  return {
    component: schedulerEvent.component as React.ComponentType<unknown>,
    fromComponent: schedulerEvent.fromComponent as React.ComponentType<unknown>,
    fromData: schedulerEvent.fromData as ScheduleBoardJobModel,
    itemData: schedulerEvent.itemData as ScheduleBoardJobModel,
    itemElement: document.createElement('div'), // Scheduler doesn't provide this
    toComponent: schedulerEvent.toComponent as React.ComponentType<unknown>,
    toData: schedulerEvent.toData as ScheduleBoardJobModel | undefined,
  };
}
